import {
  Alert,
  AlertTriangle,
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  Check,
  Clock,
  Dot,
  mergeClassName,
  Smartphone,
  X
} from '@chain-zen/ui'
import { differenceInHours, format, formatDistanceToNow, isAfter, isBefore, parseISO, subHours } from 'date-fns'

import { useShipmentContext } from '../../../contexts/shipment-context'
import {
  AuthorizationStatusEnum,
  type IDeviceInfo,
  type TLocationSettingsType,
  type TMotionActivityType
} from '../../../types/tracking'
import { StatusIcon } from '../status-icon'

const getBatteryIcon = ({ level, isCharging }: { isCharging: boolean; level: number | undefined }) => {
  if (isCharging) {
    return <BatteryCharging className="h-6 w-6" />
  }

  const batteryPercentage = level ? level * 100 : 0

  if (batteryPercentage >= 80) {
    return <BatteryFull className="h-6 w-6" />
  } else if (batteryPercentage <= 20) {
    return <BatteryLow className="h-6 w-6" />
  } else {
    return <BatteryMedium className="h-6 w-6" />
  }
}

const BatteryStatus = ({ level, isCharging }: { isCharging: boolean; level: number | undefined }) => {
  if (level === undefined || level < 0) return null

  return (
    <div className="flex items-center gap-1">
      {getBatteryIcon({ isCharging, level })}
      <span className="text-xs">{Math.round(level * 100)}%</span>
    </div>
  )
}

const DevicePlatformIcon = ({ platform, iconClassName }: { iconClassName?: string; platform: string }) => {
  const iconClass = iconClassName || 'h-6 w-6'

  if (platform === 'ios') {
    return <Smartphone className={iconClass || 'h-6 w-6'} />
  } else if (platform === 'android') {
    return <Smartphone className={iconClass} />
  }
  return <Smartphone className={iconClass} />
}

const MotionStatus = ({ activity }: { activity: TMotionActivityType | null }) => {
  if (!activity || activity === 'unknown') return null

  const motionEmoji = {
    in_vehicle: '🚚',
    on_bicycle: '🚴',
    on_foot: '👟',
    running: '🏃',
    still: '🏡',
    walking: '👟'
  }

  const formattedActivity = activity.replace(/_/g, ' ')
  const displayActivity = formattedActivity.charAt(0).toUpperCase() + formattedActivity.slice(1)

  return (
    <div className="flex items-center gap-2">
      <p className="text-xs font-semibold">
        Motion:{' '}
        <span className="font-normal">
          {motionEmoji[activity]} {displayActivity}
        </span>
      </p>
    </div>
  )
}

const DeviceStatusItem = ({ isPositive, label }: { isPositive: boolean; label: string }) => (
  <div className="flex items-center gap-2">
    <StatusIcon isPositive={isPositive} />
    <span>{label}</span>
  </div>
)

const DeviceHeader = ({ device }: { device: IDeviceInfo | null }) => (
  <div className="flex justify-between items-center w-full">
    <DevicePlatformIcon platform={device?.devicePlatform ?? ''} iconClassName="h-4 w-4" />
    <BatteryStatus level={device?.batteryLevel} isCharging={device?.isDeviceCharging ?? false} />
  </div>
)

const MobileDeviceInfo = ({
  device,
  lastSeen,
  lastMotionActivity
}: {
  device: IDeviceInfo | null
  lastMotionActivity: TMotionActivityType | null
  lastSeen: string | null
}) => (
  <div className="space-y-2">
    <h3 className="text-md font-semibold">
      {device?.deviceManufacturer || device?.devicePlatform} {device?.deviceModel} / OS{' '}
      {device?.deviceOSVersion || device?.androidSDKVersion}
    </h3>
    {!!device?.languageCode && (
      <p className="text-xs font-semibold">
        Language: <span className="font-normal">{device.languageCode}</span>
      </p>
    )}
    <MotionStatus activity={lastMotionActivity} />
    {!!lastSeen && (
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4" />
        <span>Last Seen: {formatLastSeen(lastSeen)}</span>
      </div>
    )}
  </div>
)

const DeviceStatus = ({
  device,
  hasSentMockLocations,
  locationSettings
}: {
  device: IDeviceInfo | null
  hasSentMockLocations: boolean
  locationSettings: TLocationSettingsType | null
}) => {
  const { isShipmentOwner } = useShipmentContext()

  return (
    <div className="space-y-3 py-2 w-full">
      <h4 className="font-semibold flex items-center gap-2">
        <span>📱</span>
        <span>Device Status</span>
      </h4>
      <div className="space-y-2">
        <DeviceStatusItem
          isPositive={!device?.isDeviceVirtual}
          label={device?.isDeviceVirtual ? 'Not a legitimate device' : 'Legitimate device'}
        />
        {!!hasSentMockLocations && !!isShipmentOwner && (
          <DeviceStatusItem
            isPositive={false}
            label="Fake locations detected. This means that the driver is using a fake GPS app."
          />
        )}
        {!!locationSettings?.authorizationStatus && (
          <DeviceStatusItem
            isPositive={locationSettings.authorizationStatus === AuthorizationStatusEnum.AuthorizationStatusAlways}
            label={`🔐 App Permissions: ${locationSettings.authorizationStatus === AuthorizationStatusEnum.AuthorizationStatusAlways ? 'Always (✅ Correct)' : `${locationSettings.authorizationStatus} (❌\nNeeds "Always")`}`}
          />
        )}
        <DeviceStatusItem
          isPositive={locationSettings?.locationServicesEnabled ?? false}
          label={`📍 Phone Location Services: ${locationSettings?.locationServicesEnabled ? 'On' : 'Off'}`}
        />
        <DeviceStatusItem
          isPositive={locationSettings?.gpsGeolocationProviderAvailable ?? false}
          label={`🛰️ GPS Hardware: ${locationSettings?.gpsGeolocationProviderAvailable ? 'Available' : 'Unavailable'}`}
        />
      </div>
    </div>
  )
}

const formatLastSeen = (date: string) => {
  return formatDistanceToNow(new Date(date), { addSuffix: true })
}

const formatDateDisplay = (date: string) => {
  return format(parseISO(date), "M/d 'at' h:mm a")
}

const isStationaryDevice = ({
  lastSeen,
  lastMotionActivity
}: {
  lastMotionActivity: TMotionActivityType | null
  lastSeen: string | null
}) => {
  if (!lastSeen || !lastMotionActivity) return false

  const stationaryActivities = ['still', 'on_foot', 'walking', 'running']
  const lastSeenDate = new Date(lastSeen)
  const oneHourAgo = subHours(new Date(), 1)
  const eightHoursAgo = subHours(new Date(), 8)

  return (
    stationaryActivities.includes(lastMotionActivity) &&
    isBefore(lastSeenDate, oneHourAgo) &&
    isAfter(lastSeenDate, eightHoursAgo)
  )
}

const AlertSection = ({
  lastSeen,
  locationSettings,
  isHistoricalDevice,
  lastMotionActivity,
  isTrackingOn
}: {
  isHistoricalDevice: boolean
  isTrackingOn?: boolean
  lastMotionActivity: TMotionActivityType | null
  lastSeen: string | null
  locationSettings: TLocationSettingsType | null
}) => {
  if (!lastSeen || isHistoricalDevice) return null

  const isMoreThanEightHours = differenceInHours(new Date(), new Date(lastSeen)) > 8
  const incorrectAuthorization =
    locationSettings?.authorizationStatus !== AuthorizationStatusEnum.AuthorizationStatusAlways
  const isStationary = isStationaryDevice({ lastMotionActivity, lastSeen })

  if (!isTrackingOn && !incorrectAuthorization) {
    return (
      <Alert
        variant="info"
        title="Action Required"
        description="The driver has turned the tracking off. Please call or message the driver and ask them to turn on the tracking on using the button in the upper right hand corner in the App"
        alertIcon={true}
        closeButton={false}
        autoFade={false}
        centered={false}
        className="max-w-sm"
      />
    )
  }

  if (incorrectAuthorization) {
    return (
      <Alert
        variant="warning"
        title="Action Required"
        description="This device has not been authorized to use location services. Please call the driver and ask them to set the app and phone permissions to 'Always'."
        alertIcon={true}
        closeButton={false}
        autoFade={false}
        centered={false}
        className="max-w-sm"
      />
    )
  }

  if (isMoreThanEightHours && isTrackingOn && !incorrectAuthorization) {
    return (
      <Alert
        variant="error"
        title="Device Not Detected"
        description={`The driver's device has not been detected since ${formatDateDisplay(lastSeen)}.
           Either the driver has had their phone off/has been in a low reception area for the past ${differenceInHours(new Date(), new Date(lastSeen))} hours or they deleted the app. Please
           call the driver and ask them top open the app, which will reactivate the tracking.
        `}
        alertIcon={true}
        closeButton={false}
        autoFade={false}
        centered={false}
        className="max-w-sm"
      />
    )
  }

  if (isStationary) {
    return (
      <Alert
        variant="info"
        title="Stationary Device"
        description="The driver is currently not moving. When the device is stationary, it won't emit new locations. When there is a significant movement change (>100m), then the device will start sending locations again."
        alertIcon={true}
        closeButton={false}
        autoFade={false}
        centered={false}
        className="max-w-sm "
      />
    )
  }

  return null
}

const DeviceInfoStatusIcon = ({ type }: { type: 'success' | 'warning' | 'error' }) => {
  const icons = {
    error: X,
    success: Check,
    warning: AlertTriangle
  }

  const colors = {
    error: 'text-red-500',
    success: 'text-green-500',
    warning: 'text-yellow-500'
  }

  const Icon = icons[type]
  return <Icon className={mergeClassName('h-4 w-4', colors[type])} />
}

const SeparatorDot = ({ className }: { className?: string }) => (
  <Dot className={mergeClassName('h-4 w-4 text-gray-400', className)} />
)

const isMoreThanEightHoursAgo = (dateString: string | null): boolean => {
  if (!dateString) return false
  return differenceInHours(new Date(), new Date(dateString)) > 8
}

export {
  AlertSection,
  BatteryStatus,
  DeviceHeader,
  DeviceInfoStatusIcon,
  DevicePlatformIcon,
  DeviceStatus,
  DeviceStatusItem,
  isMoreThanEightHoursAgo,
  MobileDeviceInfo,
  MotionStatus,
  SeparatorDot
}
